import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadIdeaList,
  loadIdeas,
  modifyIdea,
  removeIdea,
  saveIdea,
} from '../api/idea'
import {
  CreateIdea,
  Idea,
  IdeaFilters,
  IdeaListRequest,
  PaginatedIdeasResponse,
  PaginationMeta,
} from '../types/idea'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

// get ideas
export const getIdeas = createAsyncThunk(
  'idea/getIdeas',
  async (_, thunkApi) => {
    try {
      return await loadIdeas()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

export const getIdeaList = createAsyncThunk(
  'idea/getIdeaList',
  async (request: IdeaListRequest, thunkApi) => {
    try {
      return await loadIdeaList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// create idea
export const addIdea = createAsyncThunk(
  'idea/addIdea',
  async (idea: CreateIdea, thunkApi) => {
    try {
      return await saveIdea(idea)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// update idea
export const updateIdea = createAsyncThunk(
  'idea/updateIdea',
  async (idea: Idea, thunkApi) => {
    try {
      return await modifyIdea(idea)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

// delete idea
export const deleteIdea = createAsyncThunk(
  'idea/deleteIdea',
  async (id: number, thunkApi) => {
    try {
      return await removeIdea(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface IdeaSliceState {
  ideas: Idea[]
  pagedIdeas: Idea[]
  ideasLoading: boolean
  ideasError?: SerializedError
  filters: IdeaFilters
  pagination: PaginationMeta
  activeListRequestId?: string
}
const initialState: IdeaSliceState = {
  ideas: [],
  pagedIdeas: [],
  ideasLoading: false,
  ideasError: undefined,
  filters: {
    name: '',
    tag: '',
  },
  pagination: emptyPagination,
  activeListRequestId: undefined,
}

const ideaSlice = createSlice({
  name: 'ideas',
  initialState,
  reducers: {
    setIdeaFilters: (
      state: IdeaSliceState,
      action: PayloadAction<IdeaFilters>,
    ) => {
      state.filters = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getIdeas.fulfilled, (state, action: PayloadAction<Idea[]>) => {
      state.ideas = action.payload
      state.ideasLoading = false
      state.ideasError = undefined
    })
    builder.addCase(getIdeas.pending, (state) => {
      state.ideasLoading = true
      state.ideasError = undefined
    })
    builder.addCase(getIdeas.rejected, (state, action) => {
      state.ideasLoading = false
      state.ideasError = action.error
    })
    builder.addCase(getIdeaList.fulfilled, (
      state,
      action: PayloadAction<PaginatedIdeasResponse> & { meta: any },
    ) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.pagedIdeas = action.payload.items
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.activeListRequestId = undefined
      state.ideasLoading = false
      state.ideasError = undefined
    })
    builder.addCase(getIdeaList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.pagedIdeas = []
      state.ideasLoading = true
      state.ideasError = undefined
    })
    builder.addCase(getIdeaList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.ideasLoading = false
      state.ideasError = action.error
    })
    builder.addCase(addIdea.fulfilled, (state, action: PayloadAction<Idea>) => {
      state.ideas.unshift(action.payload)

      const matchesFilters =
        (!state.filters.name ||
          action.payload.name
            .toLocaleLowerCase()
            .includes(state.filters.name.toLocaleLowerCase())) &&
        (!state.filters.tag ||
          action.payload.tags.some((tag) =>
            tag.toLowerCase().includes(state.filters.tag.toLocaleLowerCase()),
          ))

      if (matchesFilters && state.pagination.page === 1) {
        state.pagedIdeas = [action.payload, ...state.pagedIdeas].slice(
          0,
          state.pagination.limit,
        )
        state.pagination.total += 1
        state.pagination.totalPages = Math.ceil(
          state.pagination.total / state.pagination.limit,
        )
      }

      state.ideasLoading = false
      state.ideasError = undefined
    })
    builder.addCase(addIdea.pending, (state) => {
      state.ideasLoading = true
      state.ideasError = undefined
    })
    builder.addCase(addIdea.rejected, (state, action) => {
      state.ideasLoading = false
      state.ideasError = action.error
    })
    builder.addCase(updateIdea.fulfilled, (state, action: PayloadAction<Idea>) => {
      state.ideas = state.ideas.map((idea) =>
        idea.id === action.payload.id ? action.payload : idea,
      )
      state.pagedIdeas = state.pagedIdeas.map((idea) =>
        idea.id === action.payload.id ? action.payload : idea,
      )
      state.ideasLoading = false
      state.ideasError = undefined
    })
    builder.addCase(updateIdea.pending, (state) => {
      state.ideasLoading = true
      state.ideasError = undefined
    })
    builder.addCase(updateIdea.rejected, (state, action) => {
      state.ideasLoading = false
      state.ideasError = action.error
    })
    builder.addCase(deleteIdea.fulfilled, (state, action: PayloadAction<number>) => {
      const wasOnCurrentPage = state.pagedIdeas.some(
        (idea) => idea.id === action.payload,
      )

      state.ideas = state.ideas.filter((idea) => idea.id !== action.payload)
      state.pagedIdeas = state.pagedIdeas.filter(
        (idea) => idea.id !== action.payload,
      )

      if (wasOnCurrentPage && state.pagination.total > 0) {
        state.pagination.total -= 1
        state.pagination.totalPages =
          state.pagination.total === 0
            ? 0
            : Math.ceil(state.pagination.total / state.pagination.limit)
      }

      state.ideasLoading = false
      state.ideasError = undefined
    })
    builder.addCase(deleteIdea.pending, (state) => {
      state.ideasLoading = true
      state.ideasError = undefined
    })
    builder.addCase(deleteIdea.rejected, (state, action) => {
      state.ideasLoading = false
      state.ideasError = action.error
    })
  },
})

export const { setIdeaFilters } = ideaSlice.actions
export default ideaSlice.reducer
